/*
PURPOSE: Clickable link component for auto-detected URLs in comments

USE CASES:
- Render URLs as clickable links with proper styling
- Open links in new tabs for security and UX
- Apply Coral brand styling to links
- Handle invalid URLs gracefully

DOMAIN BOUNDARIES:
- Responsible for rendering clickable links only
- Does NOT detect URLs (handled by urlProcessor utility)
- Does NOT store or manage link state
- UI component focused on presentation and interaction

ASSUMPTIONS:
- URLs have already been validated by urlProcessor
- Links should open in new tabs/windows
- Invalid URLs should render as plain text
- Styling should match Coral brand colors
*/

import React from 'react';
import { URLInfo } from '../../utils/urlProcessor';

interface AutoLinkProps {
  urlInfo: URLInfo;
  children: React.ReactNode;
  className?: string;
}

const AutoLink: React.FC<AutoLinkProps> = ({ 
  urlInfo, 
  children, 
  className = '' 
}) => {
  // If URL is invalid, render as plain text
  if (!urlInfo.isValid) {
    return <span className={className}>{children}</span>;
  }

  // Handle click to prevent any issues with malformed URLs
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Additional safety check - if URL is suspicious, prevent navigation
    if (urlInfo.url === '#' || !urlInfo.url.startsWith('http')) {
      e.preventDefault();
      console.warn('Blocked suspicious URL:', urlInfo.originalText);
    }
  };

  return (
    <a
      href={urlInfo.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      className={`text-coral-pink hover:text-coral-orange transition-colors duration-200 hover:underline ${className}`}
      title={`Open ${urlInfo.url} in new tab`}
    >
      {children}
    </a>
  );
};

export default AutoLink;